import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { useSelector } from "react-redux";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense && am5.addLicense("AM5C258896422");

const CrossSourceLineChart = ({
  chartId,
  chartClass,
  chartData,
  chartLabels,
  cardTitle,
  showScrollbar,
  visibleChartData // <-- new prop for visible series
}) => {
  useLayoutEffect(() => {
    if (!chartId || !chartData || chartData.length === 0 || !chartLabels) return;
    let root = am5.Root.new(chartId);
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        paddingTop: 10,
        paddingRight: 30,
        paddingLeft: 10,
        paddingBottom: 40
      })
    );

    // X Axis
    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "label",
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 30,
          strokeOpacity: 1,
          stroke: am5.color(0x757575),
        }),
      })
    );
    xAxis.get("renderer").grid.template.setAll({ visible: false });
    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x757575),
      fontSize: 14
    });
    xAxis.data.setAll(chartLabels.map(label => ({ label })));

    // Y Axis
    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 1,
          stroke: am5.color(0x757575),
        }),
        min: 0,
        numberFormat: "#,#a"
      })
    );
    yAxis.get("renderer").grid.template.setAll({ visible: false });
    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x757575),
      fontSize: 13
    });

    // Helper to format numbers as K/M
    function formatNumber(num) {
      if (num >= 1e6) return (num / 1e6).toFixed(2).replace(/\.00$/, "") + "M";
      if (num >= 1e3) return (num / 1e3).toFixed(2).replace(/\.00$/, "") + "K";
      return num?.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    // Use only visible series for chart and tooltip
    const seriesToShow = visibleChartData && visibleChartData.length > 0 ? visibleChartData : chartData;

    // --- Custom Styled Tooltip (like CombinedBarLineMultipleAxesChart) ---
    const createStyledTooltip = (root) => {
      const tooltip = am5.Tooltip.new(root, {
        getStrokeFromSprite: false,
        getFillFromSprite: false,
        autoTextColor: false,
      });

      tooltip.label.setAll({
        fill: am5.color(0xffffff),      // White text
        fontSize: 13,
        fontFamily: "Ubuntu",
        fontWeight: "400",
        maxWidth: 300,
        oversizedBehavior: "wrap"
      });

      tooltip.get("background").setAll({
        fill: am5.color(0x213345),      // Dark background
        stroke: am5.color(0x000000),
        strokeWidth: 1,
        cornerRadius: 8,
      });

      return tooltip;
    };

    const wideData = chartLabels.map((label, i) => {
      const obj = { label };
      seriesToShow.forEach(series => {
        obj[series.label] = series.data[i];
        obj[`${series.label}_color`] = series.borderColor;
      });
      return obj;
    });

    const styledTooltip = createStyledTooltip(root);

    styledTooltip.adapters.add("labelText", (labelText, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) return "";
      const ctx = dataItem.dataContext;
      if (!ctx) return "";

      let output = `[bold]${ctx.label}[/]\n`;
      seriesToShow.forEach((series, idx) => {
        const value = ctx[series.label];
        const color = series.borderColor || "#1976D2";
        if (value !== undefined && value !== null && value !== "") {
          output += `[font-size:13px][#${color.replace("#", "")}]●[/] [bold]${series.label}: [/][bold]${formatNumberForTooltip(value, series.label)}\n`;
        }
      });
      return output.trim();
    });

    // 3. Now create series and use styledTooltip
    seriesToShow.forEach((series, idx) => {
      const amSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: series.label,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: series.label,
          categoryXField: "label",
          stroke: am5.color(series.borderColor),
          fill: am5.color(series.borderColor),
          tension: 0.7,
          tooltip: styledTooltip // <-- use the styled tooltip here
        })
      );
      amSeries.strokes.template.setAll({ strokeWidth: 2, stroke: am5.color(series.borderColor) });
      amSeries.fills.template.setAll({
        visible: true, // Fill area for all series
        fillOpacity: 0.25, // Same opacity for all
        fill: am5.color(series.borderColor)
      });
      amSeries.data.setAll(wideData);
    });

    // Remove the amCharts default legend so only the custom legend is shown
    // let legend = chart.children.push(
    //   am5.Legend.new(root, {
    //     centerX: am5.percent(50),
    //     x: am5.percent(50),
    //     marginTop: 10,
    //     marginBottom: 10
    //   })
    // );
    // legend.data.setAll(chart.series.values);

    // Cursor
    chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "none",
      xAxis: xAxis,
      yAxis: yAxis
    }));
    chart.get("cursor").lineY.set("visible", false);

    // Optional scrollbar
    if (showScrollbar) {
      const scrollbar = am5.Scrollbar.new(root, {
        marginTop: 20,
        orientation: "horizontal",
      });
      chart.set("scrollbarX", scrollbar);
      chart.bottomAxesContainer.children.push(scrollbar);
    }

    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [chartData, chartLabels, chartId, showScrollbar, visibleChartData]);

  return (
    <Wrapper>
      <div
        id={chartId}
        className={chartClass}
        style={{
          width: "100%",
          height: "300px", // Match PremiumIndexChart height
          minHeight: "300px",
          maxWidth: "100%",
          height: "450px",
          minHeight: "450px",
        }}
      />
    </Wrapper>
  );
};

CrossSourceLineChart.propTypes = {
  chartId: PropTypes.string,
  chartClass: PropTypes.string,
  chartData: PropTypes.array,   // [{label, data: [], borderColor}]
  chartLabels: PropTypes.array, // [x axis labels]
  cardTitle: PropTypes.string,
  showScrollbar: PropTypes.bool,
  visibleChartData: PropTypes.array, // Optional: [{label, data: [], borderColor}] to show only specific series
};

export default CrossSourceLineChart;